import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { fieldMaySync, isCutOffDate, isMinimumFieldVersion } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, TextArea, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import {
  dateText,
  daysAgo,
  loadDevices,
  loadMinSyncVersion,
  loadRecommendedVersion,
  saveMinSyncVersion,
  saveRecommendedVersion,
  type AdminDevice,
  type MinSyncVersion,
  type RecommendedFieldVersionSetting,
} from './api.ts';
import { TableWrap } from '../components/TableWrap.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/**
 * Whether a device stops syncing under `minimum` (decision 497): a laptop whose last reported version is below it.
 * A laptop that reported none runs 2026.10.5 or older (the domain's rule). Mobiles do not run Teslyn Field.
 */
export const belowMinimum = (
  d: { readonly kind: 'laptop' | 'mobile'; readonly appVersion: string | null },
  minimum: string | null,
): boolean => d.kind === 'laptop' && !fieldMaySync(d.appVersion, minimum);

/**
 * Whether a laptop shows "Update Teslyn Field soon" under `recommended` (decision 523): it still syncs, and its last
 * reported version is below the recommended one.
 */
export const belowRecommended = (
  d: { readonly kind: 'laptop' | 'mobile'; readonly appVersion: string | null },
  minimum: string | null,
  recommended: string | null,
): boolean => d.kind === 'laptop' && !belowMinimum(d, minimum) && !fieldMaySync(d.appVersion, recommended);

const laptops = (n: number): string => (n === 1 ? '1 laptop' : `${n} laptops`);

/** "Below the minimum", "Update soon" or "Syncs", always as a word with its colour (brand rules). */
export function SyncVersionChip({
  below,
  soon = false,
}: {
  readonly below: boolean;
  readonly soon?: boolean;
}): ReactElement {
  if (below) return <span className="chip chip--warn label">Below the minimum</span>;
  if (soon) return <span className="chip chip--brand label">Update soon</span>;
  return <span className="chip chip--pass label">Syncs</span>;
}

/**
 * Braynewave's Devices page (decision 497): the minimum Teslyn Field version for sync, and every organisation's
 * laptops with the version each reported at its last licence check-in, how many are below the minimum, and a filter
 * for them.
 */
export function AdminDevicesScreen({ sb }: { readonly sb: SupabaseClient }): ReactElement {
  const [devices, setDevices] = useState<AdminDevice[] | null>(null);
  const [minimum, setMinimum] = useState<MinSyncVersion | null | undefined>(undefined);
  const [recommended, setRecommended] = useState<RecommendedFieldVersionSetting | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onlyBelow, setOnlyBelow] = useState(false);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);

  const reload = useCallback(() => {
    setError(null);
    Promise.all([loadDevices(sb), loadMinSyncVersion(sb), loadRecommendedVersion(sb)])
      .then(([d, m, r]) => {
        setDevices(d);
        setMinimum(m);
        setRecommended(r);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb]);
  useEffect(reload, [reload]);

  const min = minimum?.value ?? null;
  const fieldLaptops = devices?.filter((d) => d.kind === 'laptop') ?? [];
  const below = fieldLaptops.filter((d) => belowMinimum(d, min));
  const shown = (devices ?? []).filter((d) => !onlyBelow || belowMinimum(d, min));

  return (
    <section className="page">
      <PageHeader
        title="Devices"
        subtitle="Every organisation’s laptops and the Teslyn Field version each last reported."
      />
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {(devices === null || minimum === undefined) && !error && <Loading what="devices" />}
      {devices && minimum !== undefined && (
        <>
          <RecommendedPanel
            current={recommended}
            minimum={min}
            laptops={fieldLaptops}
            onSave={async (version, from, note) => {
              try {
                await saveRecommendedVersion(sb, version, from, note);
                setNotice({
                  ok: true,
                  text: version
                    ? `Recommended version saved: ${version}${from ? `, needed from ${dateText(from)}` : ''}.`
                    : 'Recommended version removed: no laptop is asked to update.',
                });
              } catch (e) {
                setNotice({ ok: false, text: message(e) });
              }
              reload();
            }}
          />
          <MinimumPanel
            current={minimum}
            laptops={fieldLaptops}
            onSave={async (value, note) => {
              try {
                await saveMinSyncVersion(sb, value, note);
                setNotice({
                  ok: true,
                  text: value ? `Minimum saved: ${value}.` : 'Minimum removed: every laptop syncs.',
                });
              } catch (e) {
                setNotice({ ok: false, text: message(e) });
              }
              reload();
            }}
          />
          <div className="panel stack">
            <h2 className="heading">Laptops and mobiles</h2>
            <p className="body">
              {min
                ? `${below.length} of ${laptops(fieldLaptops.length)} are below the minimum ${min}.`
                : `${below.length} of ${laptops(fieldLaptops.length)} are below the minimum.`}
            </p>
            <CheckField label="Below the minimum only" checked={onlyBelow} onChange={setOnlyBelow} />
            {shown.length === 0 ? (
              <p className="body muted">
                {onlyBelow ? 'No laptop is below the minimum.' : 'No devices yet.'}
              </p>
            ) : (
              <TableWrap>
                <table className="table" aria-label="Devices">
                  <thead>
                    <tr>
                      <th className="label">Organisation</th>
                      <th className="label">Person</th>
                      <th className="label">Device</th>
                      <th className="label">Teslyn Field</th>
                      <th className="label">Last check-in</th>
                      <th className="label">Sync</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((d) => (
                      <tr key={d.id}>
                        <td>{d.tenantName}</td>
                        <td>
                          {d.person}
                          {d.email && (
                            <>
                              <br />
                              <span className="caption muted data">{d.email}</span>
                            </>
                          )}
                        </td>
                        <td>
                          <span>{d.label || '(no name)'}</span>{' '}
                          <span className="caption muted">{d.kind === 'laptop' ? 'Laptop' : 'Mobile'}</span>
                        </td>
                        <td className={d.appVersion ? 'data' : 'muted'}>{d.appVersion ?? 'Not reported'}</td>
                        <td className="caption">{daysAgo(d.lastCheckinAt)}</td>
                        <td>
                          {d.kind === 'laptop' ? (
                            <SyncVersionChip
                              below={belowMinimum(d, min)}
                              soon={belowRecommended(d, min, recommended?.version ?? null)}
                            />
                          ) : (
                            '–'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            )}
            <p className="caption muted">
              The version is the one Teslyn Field reported at its last licence check-in. A laptop that
              reported none runs 2026.10.5 or older.
            </p>
          </div>
        </>
      )}
    </section>
  );
}

function RecommendedPanel({
  current,
  minimum,
  laptops: all,
  onSave,
}: {
  readonly current: RecommendedFieldVersionSetting | null;
  readonly minimum: string | null;
  readonly laptops: readonly AdminDevice[];
  readonly onSave: (version: string | null, from: string | null, note: string) => Promise<void>;
}): ReactElement {
  const [value, setValue] = useState('');
  const [from, setFrom] = useState('');
  const [note, setNote] = useState('');
  const [issue, setIssue] = useState<string | null>(null);
  const typed = value.trim();
  const day = from.trim();
  const asked = (version: string): number => all.filter((d) => belowRecommended(d, minimum, version)).length;
  const now = current?.version ?? null;

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (!isMinimumFieldVersion(typed))
      return setIssue('Enter a release version such as 2026.10.7 (year, month and patch; no beta).');
    if (minimum && !fieldMaySync(typed, minimum))
      return setIssue(`The recommended version must be at or above the minimum ${minimum}.`);
    if (day && !isCutOffDate(day)) return setIssue('Enter the date as a day, such as 2026-11-01.');
    setIssue(null);
    void onSave(typed, day || null, note.trim()).then(() => {
      setValue('');
      setFrom('');
      setNote('');
    });
  }

  return (
    <form className="panel stack" onSubmit={submit} noValidate aria-label="Recommended Teslyn Field version">
      <h2 className="heading">Recommended Teslyn Field version</h2>
      <p className="body">
        {now ? (
          <>
            <span className="data">{now}</span>
            {current?.from ? `, needed from ${dateText(current.from)}` : ', no date set'}
            {current?.note ? ` (${current.note})` : ''}.
          </>
        ) : (
          'No recommended version: no laptop is asked to update.'
        )}
      </p>
      {now && (
        <p className="body">
          {`${asked(now)} of ${laptops(all.length)} run an older version and show “Update Teslyn Field soon”.`}
        </p>
      )}
      <p className="caption muted">
        Laptops below it show “Update Teslyn Field soon: version … is needed from …” and keep syncing. Set it
        first, wait while laptops update, then raise the minimum.
      </p>
      <div className="form-grid">
        <TextField
          label="Recommended version"
          spellCheck={false}
          hint="For example 2026.10.7"
          value={value}
          onChange={setValue}
        />
        <TextField
          label="Needed from (optional)"
          hint="The day you plan to raise the minimum"
          type="date"
          value={from}
          onChange={setFrom}
        />
      </div>
      {isMinimumFieldVersion(typed) && (
        <p className="body" role="status">
          {`With ${typed}, ${asked(typed)} of ${laptops(all.length)} show “Update Teslyn Field soon” and keep syncing.`}
        </p>
      )}
      <TextArea label="Note for the audit log" value={note} onChange={setNote} rows={2} />
      {issue && (
        <p className="field__error caption" role="alert">
          {issue}
        </p>
      )}
      <div className="row">
        <Button type="submit" variant="primary">
          Save recommended version
        </Button>
        {now && (
          <Button variant="quiet" onClick={() => void onSave(null, null, note.trim())}>
            Remove the recommended version
          </Button>
        )}
      </div>
    </form>
  );
}

function MinimumPanel({
  current,
  laptops: all,
  onSave,
}: {
  readonly current: MinSyncVersion | null;
  readonly laptops: readonly AdminDevice[];
  readonly onSave: (value: string | null, note: string) => Promise<void>;
}): ReactElement {
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [issue, setIssue] = useState<string | null>(null);
  const typed = value.trim();
  const stopping = isMinimumFieldVersion(typed) ? all.filter((d) => belowMinimum(d, typed)).length : null;

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (!isMinimumFieldVersion(typed))
      return setIssue('Enter a release version such as 2026.10.6 (year, month and patch; no beta).');
    setIssue(null);
    void onSave(typed, note.trim()).then(() => {
      setValue('');
      setNote('');
    });
  }

  return (
    <form
      className="panel stack"
      onSubmit={submit}
      noValidate
      aria-label="Minimum Teslyn Field version for sync"
    >
      <h2 className="heading">Minimum Teslyn Field version for sync</h2>
      <p className="body">
        {current?.value ? (
          <>
            <span className="data">{current.value}</span> since {dateText(current.updatedAt)}
            {current.note ? ` (${current.note})` : ''}. Laptops below it keep working offline and show “Update
            Teslyn Field to keep syncing”; their changes go to Teslyn Cloud after the update.
          </>
        ) : (
          'No minimum: every laptop syncs.'
        )}
      </p>
      <p className="caption muted">
        Raise it when a Teslyn Cloud change needs a newer Teslyn Field, once that version is released.
      </p>
      <div className="form-grid">
        <TextField
          label="Minimum version"
          spellCheck={false}
          hint="For example 2026.10.6"
          value={value}
          onChange={setValue}
        />
      </div>
      {stopping !== null && (
        <p className="body" role="status">
          {`With ${typed}, ${stopping} of ${laptops(all.length)} stop syncing until they are updated.`}
        </p>
      )}
      <TextArea label="Note for the audit log" value={note} onChange={setNote} rows={2} />
      {issue && (
        <p className="field__error caption" role="alert">
          {issue}
        </p>
      )}
      <div className="row">
        <Button type="submit" variant="primary">
          Save minimum
        </Button>
        {current?.value && (
          <Button variant="quiet" onClick={() => void onSave(null, note.trim())}>
            Remove the minimum
          </Button>
        )}
      </div>
    </form>
  );
}
